import { Nullable } from '@ps/utils';
import {
  FilterAudienceForm,
  INITIAL_RESPONDENT_LIST_UPLOAD_STATE,
  RespondentListUploadState,
  SurveyLaunchFormState,
} from '@ps/survey-utils';
import {
  DEFAULT_CHILD_LAUNCH_STATE,
  DEFAULT_SURVEY_CHILD_KEY,
} from '../../survey-creation-state.constants';
import { SurveyCreationLaunchState } from '../../survey-creation-state.model';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import {
  ChildMapPatch,
  patchChildrenMap,
  resolveActiveChildKey as resolveActiveChildKeyOf,
  SurveyChildScope,
} from '../../survey-creation-child-registry.helper';

/**
 * A fresh launch state targeting `rootSurveyId` — mirrors
 * `initializeCoreState`: the one child is keyed by the root id directly in
 * edit mode, else the empty-string pre-draft placeholder.
 */
export function initializeLaunchState(
  rootSurveyId: Nullable<number>,
): SurveyCreationLaunchState {
  const childKey = rootSurveyId ?? DEFAULT_SURVEY_CHILD_KEY;

  return {
    children: {
      [childKey]: DEFAULT_CHILD_LAUNCH_STATE,
    },
    respondentListUploads: {
      [childKey]: { ...INITIAL_RESPONDENT_LIST_UPLOAD_STATE },
    },
  };
}

export function resolveActiveChildKey(
  state: SurveyCreationLaunchState,
  activeChildKey: Nullable<SurveyChildKey>,
): SurveyChildKey {
  return resolveActiveChildKeyOf(
    state.children,
    activeChildKey,
    DEFAULT_SURVEY_CHILD_KEY,
  );
}

export function patchChildFilterAudience(
  child: SurveyLaunchFormState,
  filterAudience: Partial<FilterAudienceForm>,
): SurveyLaunchFormState {
  return {
    value: {
      ...child.value,
      filterAudience: { ...child.value.filterAudience, ...filterAudience },
    },
    valid: child.valid,
    errors: child.errors,
    isDirty: child.isDirty,
  };
}

export type ChildPatch = ChildMapPatch<SurveyLaunchFormState>;

export function patchChildren(
  state: SurveyCreationLaunchState,
  activeChildKey: Nullable<SurveyChildKey>,
  scope: SurveyChildScope,
  patch: ChildPatch,
): SurveyCreationLaunchState {
  return {
    ...state,
    children: patchChildrenMap(
      state.children,
      activeChildKey,
      scope,
      patch,
      DEFAULT_CHILD_LAUNCH_STATE,
    ),
  };
}

export type RespondentListUploadPatch =
  ChildMapPatch<RespondentListUploadState>;

export function patchRespondentListUploads(
  state: SurveyCreationLaunchState,
  activeChildKey: Nullable<SurveyChildKey>,
  scope: SurveyChildScope,
  patch: RespondentListUploadPatch,
): SurveyCreationLaunchState {
  return {
    ...state,
    respondentListUploads: patchChildrenMap(
      state.respondentListUploads,
      activeChildKey,
      scope,
      patch,
      INITIAL_RESPONDENT_LIST_UPLOAD_STATE,
    ),
  };
}
